import { useState, useEffect, useCallback } from "react";
import { useSession } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import CommentSection from "../components/dashboard/CommentSection";
import Header from "../components/layout/Header";
import Sidebar from "../components/layout/Sidebar";
import api from "../services/api";

interface Thread {
  id: string;
  taskId: string;
  userId: string;
  studentName: string;
  studentEmail: string;
  taskTitle: string;
  priority: string;
  commentCount: number;
  unreadCount: number;
  status: string;
  updatedAt: string;
}

export default function DiscussionsPage() {
  const { data: session, status } = useSession();
  const navigate = useNavigate();
  const userId = session?.user?.id;
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [loading, setLoading] = useState(true);

  // Advanced Filters State
  const [searchEmail, setSearchEmail] = useState("");
  const [debouncedEmail, setDebouncedEmail] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Pagination State
  const [page, setPage] = useState(1);
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
  const [prevPageUrl, setPrevPageUrl] = useState<string | null>(null);

  // Task Details Modal State
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskDetails, setTaskDetails] = useState<any>(null);
  const [loadingTask, setLoadingTask] = useState(false);

  useEffect(() => {
    if (status === "unauthenticated") {
      navigate("/login");
    }
  }, [status, navigate]);

  // Debounce email search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEmail(searchEmail);
      setPage(1); // Reset page on new search
    }, 500);
    return () => clearTimeout(handler);
  }, [searchEmail]);

  // Reset page when other filters change
  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, unreadOnly]);

  const fetchThreads = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        t: Date.now().toString()
      });
      if (debouncedEmail) params.append("email", debouncedEmail);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (unreadOnly) params.append("unread_only", "1");

      const res = await api.get(`/discussions/threads/${userId}?${params.toString()}`);
      
      const data = res.data.data || [];
      setThreads(data);
      setNextPageUrl(res.data.next_page_url);
      setPrevPageUrl(res.data.prev_page_url);

      setSelectedThread(prev => {
        if (data.length === 0) return null;
        if (!prev) return data[0];
        return prev;
      });
    } catch (err) {
      console.error("Failed to fetch threads:", err);
    } finally {
      setLoading(false);
    }
  }, [userId, page, debouncedEmail, startDate, endDate, unreadOnly]);

  useEffect(() => {
    fetchThreads();
    
    const handleRead = () => fetchThreads();
    window.addEventListener("discussionRead", handleRead);
    return () => window.removeEventListener("discussionRead", handleRead);
  }, [fetchThreads]);

  const loadTaskDetails = async () => {
    if (!selectedThread) return;
    setIsTaskModalOpen(true);
    // Cache check
    if (taskDetails && taskDetails.id === selectedThread.taskId) return;
    
    setLoadingTask(true);
    try {
      const res = await api.get(`/tasks/${selectedThread.taskId}`);
      setTaskDetails(res.data);
    } catch (err) {
      console.error("Failed to load task details:", err);
    } finally {
      setLoadingTask(false);
    }
  };

  if (status === "loading") return null;

  return (
    <div className="min-h-screen bg-background text-on-background flex flex-col h-screen overflow-hidden">
      <Header />
      <Sidebar />

      <main className="flex-1 flex ml-0 md:ml-20 mt-16 overflow-hidden">
        {/* Threads List (Left) */}
        <div className="w-full md:w-[380px] border-r border-outline-variant/10 bg-surface-container-low flex flex-col h-full overflow-hidden shrink-0">
          <div className="p-6 border-b border-outline-variant/10">
            <h2 className="text-xl font-black text-on-surface tracking-tighter italic">Task <span className="text-primary">Discussions.</span></h2>
            <p className="text-[10px] font-bold text-on-surface-variant/40 uppercase tracking-widest mt-1 mb-4">Protocol Communication Hub</p>
            
            <div className="space-y-3">
              <div className="relative w-full">
                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant/50 text-sm">search</span>
                <input 
                  type="text" 
                  placeholder="Search by email..." 
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="pl-10 pr-10 py-2.5 rounded-[1.5rem] bg-surface-container-high border border-outline-variant/10 text-xs font-bold text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:border-primary transition-colors w-full"
                />
                <button 
                  onClick={() => setShowFilters(!showFilters)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors ${showFilters ? 'bg-primary/20 text-primary' : 'hover:bg-on-surface-variant/10 text-on-surface-variant'}`}
                >
                  <span className="material-symbols-outlined text-sm">tune</span>
                </button>
              </div>

              {/* Collapsible Advanced Filters */}
              {showFilters && (
                <div className="p-4 bg-surface-container-highest/30 rounded-2xl border border-outline-variant/5 space-y-3 animate-in fade-in slide-in-from-top-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">Unread Only</label>
                    <button 
                      onClick={() => setUnreadOnly(!unreadOnly)}
                      className={`w-10 h-5 rounded-full relative transition-colors ${unreadOnly ? 'bg-primary' : 'bg-surface-container-highest border border-outline-variant/20'}`}
                    >
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow-sm ${unreadOnly ? 'left-[22px]' : 'left-0.5'}`} />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] font-black text-on-surface-variant/60 uppercase tracking-widest mb-1 block">Start Date</label>
                      <input 
                        type="date" 
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        className="w-full bg-surface-container-high border border-outline-variant/10 rounded-xl px-3 py-2 text-xs font-bold text-on-surface focus:outline-none focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="text-[9px] font-black text-on-surface-variant/60 uppercase tracking-widest mb-1 block">End Date</label>
                      <input 
                        type="date" 
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        className="w-full bg-surface-container-high border border-outline-variant/10 rounded-xl px-3 py-2 text-xs font-bold text-on-surface focus:outline-none focus:border-primary"
                      />
                    </div>
                  </div>
                  
                  {(startDate || endDate || unreadOnly) && (
                    <button 
                      onClick={() => { setStartDate(""); setEndDate(""); setUnreadOnly(false); setSearchEmail(""); }}
                      className="w-full py-2 text-[10px] font-black uppercase tracking-widest text-on-surface-variant hover:text-error transition-colors"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2">
            {loading ? (
              <div className="space-y-3 p-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 bg-surface-container-high animate-pulse rounded-2xl" />
                ))}
              </div>
            ) : threads.length > 0 ? (
              threads.map((thread) => (
                <button
                  key={thread.id}
                  onClick={() => setSelectedThread(thread)}
                  className={`w-full text-left p-5 rounded-[1.75rem] border transition-all relative overflow-hidden group ${selectedThread?.id === thread.id
                      ? "bg-primary/10 text-primary border-primary/20 shadow-md translate-x-1"
                      : "bg-surface-container-highest/30 border-transparent hover:bg-surface-container-high hover:translate-x-0.5"
                    }`}
                >
                  <div className="flex justify-between items-start mb-2 relative z-10">
                    <span className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter ${
                        thread.priority === "VIP" ? "bg-cyan-500 text-white" :
                        thread.priority === "PREMIUM" ? "bg-amber-500 text-white" :
                        "bg-slate-500 text-white"
                      }`}>
                      {thread.priority}
                    </span>
                    <span className={`text-[9px] font-bold uppercase tracking-widest ${selectedThread?.id === thread.id ? "text-primary/40" : "text-on-surface-variant/40"}`}>
                      {new Date(thread.updatedAt).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="relative z-10 flex justify-between items-center pr-2">
                    <div className="overflow-hidden">
                      <h4 className={`text-sm font-black tracking-tight leading-tight mb-1 truncate ${selectedThread?.id === thread.id ? "text-primary" : "text-on-surface"}`}>
                        {thread.taskTitle}
                      </h4>
                      <p className={`text-[10px] font-medium opacity-60 ${selectedThread?.id === thread.id ? "text-primary/70" : "text-on-surface-variant"}`}>
                        Client: {thread.studentName}
                      </p>
                    </div>

                    {thread.unreadCount > 0 && (
                      <div className="flex-shrink-0 w-5 h-5 bg-red-500 text-white text-[9px] font-black rounded-full flex items-center justify-center shadow-lg border-2 border-surface animate-pulse ml-2">
                        {thread.unreadCount}
                      </div>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-12 text-center space-y-4 opacity-30 mt-20">
                <span className="material-symbols-outlined text-6xl">forum</span>
                <p className="text-xs font-bold uppercase tracking-widest">No active threads</p>
              </div>
            )}
          </div>
          
          {/* Pagination Controls */}
          {(prevPageUrl || nextPageUrl) && (
            <div className="p-4 border-t border-outline-variant/10 bg-surface-container-lowest flex items-center justify-between">
              <button 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={!prevPageUrl}
                className="p-2 rounded-xl bg-surface-container-high disabled:opacity-30 hover:bg-primary/10 transition-colors flex items-center justify-center text-on-surface-variant"
              >
                <span className="material-symbols-outlined text-sm">chevron_left</span>
              </button>
              <span className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant/50">
                Page {page}
              </span>
              <button 
                onClick={() => setPage(p => p + 1)}
                disabled={!nextPageUrl}
                className="p-2 rounded-xl bg-surface-container-high disabled:opacity-30 hover:bg-primary/10 transition-colors flex items-center justify-center text-on-surface-variant"
              >
                <span className="material-symbols-outlined text-sm">chevron_right</span>
              </button>
            </div>
          )}
        </div>

        {/* Chat Pane (Right) */}
        <div className="flex-1 bg-surface flex flex-col h-full overflow-hidden hidden md:flex">
          {selectedThread ? (
            <div className="flex-1 flex flex-col h-full overflow-hidden">
              {/* Chat Header */}
              <div className="p-6 sm:p-8 border-b border-outline-variant/10 flex items-center justify-between bg-surface-container-lowest/50 backdrop-blur-md z-10">
                <div className="flex items-center gap-4 sm:gap-6 min-w-0">
                  <div className="w-12 h-12 rounded-[1rem] bg-on-surface text-surface flex items-center justify-center font-black text-xl shadow-lg flex-shrink-0">
                    {selectedThread?.studentName?.[0] || "U"}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-0.5">
                      <h3 className="text-base sm:text-lg font-black text-on-surface tracking-tighter truncate">{selectedThread.studentName}</h3>
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                    </div>
                    <p className="text-[10px] font-bold text-on-surface-variant/60 uppercase tracking-widest">
                      Protocol ID: #{selectedThread.id.slice(-8).toUpperCase()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="text-right hidden sm:block">
                    <p className="text-[9px] font-black text-on-surface-variant/40 uppercase tracking-widest mb-1">Current Status</p>
                    <span className="text-[10px] font-black bg-surface-container-high px-4 py-2 rounded-full uppercase tracking-[0.15em] border border-outline-variant/10">
                      {selectedThread.status}
                    </span>
                  </div>
                  <button 
                    onClick={loadTaskDetails}
                    title="View Task Details"
                    className="w-10 h-10 rounded-full bg-surface-container-high hover:bg-primary/10 hover:text-primary transition-colors flex items-center justify-center text-on-surface-variant border border-outline-variant/10"
                  >
                    <span className="material-symbols-outlined text-lg">info</span>
                  </button>
                </div>
              </div>

              {/* Discussion Area */}
              <div className="flex-1 p-4 sm:p-6 lg:p-10 bg-surface overflow-hidden">
                <div className="h-full max-w-5xl mx-auto flex flex-col">
                  <CommentSection submissionId={selectedThread.id} hideHeader={true} />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center space-y-6 px-6">
              <div className="w-24 h-24 sm:w-32 sm:h-32 bg-surface-container-high rounded-[3rem] flex items-center justify-center">
                <span className="material-symbols-outlined text-5xl sm:text-6xl text-on-surface-variant/20">chat_bubble</span>
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-xl sm:text-2xl font-black text-on-surface tracking-tighter opacity-40 italic">Select a task <span className="text-primary">to discuss.</span></h3>
                <p className="text-[10px] font-bold text-on-surface-variant/30 uppercase tracking-[0.2em]">Execution Protocol Channel</p>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Task Details Modal */}
      {isTaskModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-surface-container-highest/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-surface w-full max-w-2xl rounded-[2rem] shadow-2xl border border-outline-variant/10 flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-outline-variant/10 bg-surface-container-lowest relative z-10">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <span className="material-symbols-outlined">assignment</span>
                </div>
                <div>
                  <h3 className="text-lg font-black text-on-surface tracking-tighter">Task Details</h3>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/60">
                    {selectedThread?.taskTitle}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setIsTaskModalOpen(false)}
                className="w-10 h-10 rounded-full hover:bg-surface-container-high transition-colors flex items-center justify-center text-on-surface-variant"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-6 sm:p-8 relative">
              {loadingTask ? (
                <div className="space-y-4">
                  <div className="h-6 w-3/4 bg-surface-container-high animate-pulse rounded-md" />
                  <div className="h-4 w-full bg-surface-container-high animate-pulse rounded-md" />
                  <div className="h-4 w-5/6 bg-surface-container-high animate-pulse rounded-md" />
                  <div className="h-32 w-full bg-surface-container-high animate-pulse rounded-xl mt-6" />
                </div>
              ) : taskDetails ? (
                <div className="space-y-8">
                  <div>
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant mb-3 flex items-center gap-2">
                      <span className="material-symbols-outlined text-sm">description</span>
                      Instructions
                    </h4>
                    <div 
                      className="prose prose-sm max-w-none prose-p:leading-relaxed prose-p:text-on-surface-variant prose-headings:text-on-surface prose-strong:text-on-surface prose-a:text-primary"
                      dangerouslySetInnerHTML={{ __html: taskDetails.description || "No instructions provided." }}
                    />
                  </div>

                  {taskDetails.guidelines && (
                    <div className="bg-surface-container-lowest p-6 rounded-2xl border border-outline-variant/10">
                      <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary mb-3 flex items-center gap-2">
                        <span className="material-symbols-outlined text-sm">lightbulb</span>
                        Guidelines & Expectations
                      </h4>
                      <div 
                        className="text-sm leading-relaxed text-on-surface-variant whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ __html: taskDetails.guidelines }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 opacity-50">
                  <span className="material-symbols-outlined text-4xl mb-2">error</span>
                  <p className="text-sm font-bold">Failed to load task details.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


